import { useState, useEffect } from "react";
import {
  Modal,
  Input,
  Button,
  DatePicker,
  Select,
  Checkbox,
  Space,
  Typography,
  Divider,
  Tag,
  ConfigProvider,
} from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { updateDocDatabase } from "../database/index";
import { openErrorNotification } from "./Notifications/errorNotification";
import { arrayUnion } from "firebase/firestore";
import { getDataDoc } from "../database/index";
import CatalogElev from "../Pages/Elevi/CatalogElev";
import "./ModalAddGradeModern.css";
const { TextArea } = Input;
const dateFormat = "DD/MM/YYYY";

function ModalTabularGrade({
  open,
  setOpen,
  eleviData,
  materii,
  gradesElevi,
  fullAcces,
  dupaTermen,
  scutiri,
  classData,
  classId,
  diriginteEmail,
}) {
  const user = useSelector((state) => state.user);
  const settings = useSelector((state) => state.settings);

  const [materieId, setMaterieId] = useState(materii?.[0]?.id);
  const [date, setDate] = useState(dayjs(new Date()));
  const [tip, setTip] = useState("absenta");
  const [studentGrades, setStudentGrades] = useState({});
  const [loading, setLoading] = useState(false);
  const [comentariu, setComentariu] = useState("");
  const [notare, setNotare] = useState(true);
  const [step, setStep] = useState(1); // 1 = student selection, 2 = date and confirmation
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setStudentGrades({});
      setDate(dayjs(new Date()));
      setTip("absenta");
      setComentariu("");
      setStep(1);
    }
  }, [open]);

  // Update notare based on selected subject and reset grades
  useEffect(() => {
    setNotare(
      materii?.find((ma) => ma.id === materieId)?.notare === undefined
        ? true
        : materii?.find((ma) => ma.id === materieId)?.notare
    );
    setStudentGrades({}); // Reset grades when subject changes
  }, [materieId]);

  // Reset grades when tip changes
  useEffect(() => {
    setStudentGrades({});
    setStep(1);
  }, [tip]);

  // Get accessible students based on teacher permissions
  const getEleviAccesibili = () => {
    // Filtrează mai întâi elevii transferați/retrași
    const eleviFiltrati = eleviData?.filter((elev) => {
      return elev.retras !== true && elev.mutat !== true;
    }) || [];

    if (fullAcces === true) {
      return eleviFiltrati;
    }
    const predaInClasa = classData?.materii?.some((m) =>
      (m?.profesori || []).includes(user.id || user.uid)
    );

    if (predaInClasa) {
      return eleviFiltrati;
    }

    return eleviFiltrati.filter((elev) => {
      const materiiSpecializate = elev?.materiiSpecialitate || [];
      return materiiSpecializate.some((ms) =>
        (ms?.profesori || []).includes(user.id || user.uid)
      );
    });
  };

  // Get subject rubrics for display
  const getSubjectRubrics = () => {
    const selectedMaterie = materii?.find((ma) => ma?.id === materieId);
    return selectedMaterie?.rubrici || [];
  };

  // Handle individual student input changes
  const handleStudentChange = (elevId, field, value) => {
    setStudentGrades((prev) => ({
      ...prev,
      [elevId]: {
        ...prev[elevId],
        [field]: value,
      },
    }));
  };

  // Handle batch assignment
  const handleBatchAssignment = async () => {
    try {
      setLoading(true);

      if (!(materieId?.length > 0)) {
        setLoading(false);
        openErrorNotification("Selectează o materie");
        return;
      }

      if (!date.isValid()) {
        setLoading(false);
        openErrorNotification("Data este invalida");
        return;
      }

      const studentsToUpdate = Object.keys(studentGrades).filter((elevId) => {
        const studentData = studentGrades[elevId];
        return tip === "absenta" ? studentData?.absent : studentData?.nota;
      });

      if (studentsToUpdate?.length === 0) {
        setLoading(false);
        openErrorNotification("Nu ai selectat niciun elev pentru actualizare");
        return;
      }

      // Process each student
      for (const elevId of studentsToUpdate) {
        const studentData = studentGrades[elevId];

        let updateObj = {
          date: date.valueOf(),
          tip,
          id: Date.now() + "dd" + elevId,
          timestamp: Date.now(),
          comentariu,
          materieId,
          authorId: user.uid,
          author: user.displayName,
          displayName: user.numeDeFamilie + " " + user.prenume,
          photoURL: user.photoLink || "",
        };

        if (tip === "nota" && studentData?.nota) {
          // Validate grade
          if (
            notare === true &&
            !(studentData.nota > 0 && studentData.nota <= 10)
          ) {
            openErrorNotification(
              `Nota pentru ${
                eleviData?.find((e) => e.id === elevId)?.numeDeFamilie
              } nu este validă`
            );
            continue;
          }
          if (
            notare === false &&
            !["FB", "I", "B", "S"].includes(studentData.nota)
          ) {
            openErrorNotification(
              `Calificativul pentru ${
                eleviData?.find((e) => e.id === elevId)?.numeDeFamilie
              } nu este valid`
            );
            continue;
          }
          updateObj["nota"] = studentData.nota;
        }

        // Update student document
        await updateDocDatabase("catalog", elevId, {
          note: arrayUnion({
            ...updateObj,
          }),
        });

        // Add to changelog
        let now = new Date();
        let onejan = new Date(now.getFullYear(), 0, 1);
        let week = Math.ceil(
          ((now.getTime() - onejan.getTime()) / 86400000 +
            onejan.getDay() +
            1) /
            7
        );

        await updateDocDatabase("changelog", classData.id + "week" + week, {
          changelog: arrayUnion({
            author: user.displayName,
            time: Date.now(),
            classId,
            elevId,
            nota: updateObj,
          }),
        });

        // Add to student report
        try {
          let dataElev = await getDataDoc("elevi", elevId);
          const gradeInfo = {
            tip: updateObj.tip,
            nota: updateObj.nota || null,
            materieNume: materii?.find((ma) => ma.id === materieId)
              ?.numeMaterie,
            data: updateObj.date,
            comentariu: updateObj.comentariu || "",
            profesor: updateObj.displayName || updateObj.author,
            timestamp: Date.now(),
            id: updateObj.id,
          };

          const recipients = [
            dataElev.adresaEmail,
            ...(dataElev.parintii || []),
          ].filter((email) => email);

          await updateDocDatabase("studentReports", elevId, {
            elevId,
            studentName: `${dataElev.numeDeFamilie} ${dataElev.prenume}`,
            className: `${classData?.anClasa || ""}-${
              classData?.identificator || ""
            }`,
            recipients,
            pendingGrades: arrayUnion(gradeInfo),
            lastUpdate: Date.now(),
          });
        } catch (error) {
          console.error("Eroare la adăugarea în raportul elevului:", error);
        }
      }

      // Crearea mesajului de succes
      const message = `${studentsToUpdate?.length} ${
        tip === "nota" ? "note" : "absențe"
      } adăugate cu succes!`;

      setSuccessMessage(message);
      setShowSuccessOverlay(true);

      // Ascunderea overlay-ului după 3 secunde
      setTimeout(() => {
        setShowSuccessOverlay(false);
        setSuccessMessage("");
        setStudentGrades({});
        setOpen(false);
      }, 3000);

      setLoading(false);
    } catch (err) {
      openErrorNotification(err.message);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setStudentGrades({});
    setShowSuccessOverlay(false);
    setSuccessMessage("");
    setOpen(false);
  };

  const eleviAccesibili = getEleviAccesibili();
  const rubrici = getSubjectRubrics();

  const dataSource = eleviAccesibili.map((elev) => ({
    key: elev.id,
    id: elev.id,
    numeDeFamilie: elev.numeDeFamilie,
    prenume: elev.prenume,
    initiala: elev.initiala,
  }));

  return (
    <div>
      <Modal
        className="modern-add-grade-modal"
        title={`Atribuire ${
          tip === "nota" ? "Note" : "Absențe"
        } - Mod Tabular ${step === 1 ? "(Pasul 1/2)" : "(Pasul 2/2)"}`}
        open={open}
        onCancel={handleCancel}
        confirmLoading={loading}
        width={window.innerWidth <= 768 ? "100vw" : "95vw"}
        style={{
          maxWidth: window.innerWidth <= 768 ? "none" : "1400px",
          top: window.innerWidth <= 768 ? 0 : 20,
        }}
        bodyStyle={{
          height:
            window.innerWidth <= 768
              ? "calc(100vh - 180px)"
              : "calc(100vh - 200px)",
          overflow: "auto",
          padding: window.innerWidth <= 768 ? "12px" : "24px",
        }}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Anulează
          </Button>,
          ...(step === 2
            ? [
                <Button key="back" onClick={() => setStep(1)}>
                  Înapoi
                </Button>,
              ]
            : []),
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={step === 1 ? () => setStep(2) : handleBatchAssignment}
            disabled={Object.keys(studentGrades).length === 0}
          >
            {step === 1
              ? "Mai Departe"
              : `Atribuie ${tip === "nota" ? "Notele" : "Absențele"}`}
          </Button>,
        ]}
      >
        {/* Loading Overlay */}
        {loading && (
          <div
            className="success-overlay"
            style={{ background: "rgba(0, 0, 0, 0.7)" }}
          >
            <div className="success-content">
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>⏳</div>
              <div className="success-message">Se procesează...</div>
            </div>
          </div>
        )}

        {/* Success Overlay */}
        {showSuccessOverlay && (
          <div className="success-overlay">
            <div className="success-content">
              <div className="success-checkmark">✓</div>
              <div className="success-message">{successMessage}</div>
            </div>
          </div>
        )}

        {step === 1 ? (
          // Step 1: Student Selection
          <>
            {/* Header */}
            <div
              style={{
                paddingBottom: "8px",
                borderBottom: "2px solid #f0f0f0",
                marginBottom: "8px",
              }}
            >
              {/* Subject Selection */}
              <div style={{ marginBottom: "8px" }}>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Materia:
                </label>
                <Select
                  style={{ width: "100%" }}
                  value={materieId}
                  onChange={setMaterieId}
                  options={materii?.map((ma) => ({
                    label: ma?.numeMaterie,
                    value: ma?.id,
                  }))}
                />
              </div>

              {/* Type Selection */}
              <div>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "6px",
                    display: "block",
                  }}
                >
                  Tip:
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button
                    type={tip === "absenta" ? "primary" : "default"}
                    onClick={() => setTip("absenta")}
                    style={{
                      backgroundColor:
                        tip === "absenta" ? "#ff4d4f" : "transparent",
                      borderColor: "#ff4d4f",
                      color: tip === "absenta" ? "#fff" : "#ff4d4f",
                      fontWeight: "600",
                      flex: 1,
                    }}
                  >
                    Absențe
                  </Button>
                  <Button
                    type={tip === "nota" ? "primary" : "default"}
                    onClick={() => setTip("nota")}
                    style={{
                      backgroundColor:
                        tip === "nota" ? "#52c41a" : "transparent",
                      borderColor: "#52c41a",
                      color: tip === "nota" ? "#fff" : "#52c41a",
                      fontWeight: "600",
                      flex: 1,
                    }}
                  >
                    Note
                  </Button>
                </div>
              </div>
            </div>

            {/* Students Cards - Full Height */}
            <div
              style={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                paddingRight: "4px",
              }}
            >
              {dataSource.map((student) => (
                <div
                  key={student.id}
                  style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: window.innerWidth <= 768 ? "8px" : "12px",
                    padding: window.innerWidth <= 768 ? "8px" : "12px",
                    backgroundColor: "#fafafa",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  {/* Student Name */}
                  <div
                    style={{
                      fontWeight: "bold",
                      marginBottom: window.innerWidth <= 768 ? "8px" : "12px",
                      fontSize: window.innerWidth <= 768 ? "16px" : "18px",
                      color: "#1890ff",
                    }}
                  >
                    {student.numeDeFamilie} {student.prenume}
                  </div>

                  {/* Existing Grades */}
                  <div
                    style={{
                      marginBottom: window.innerWidth <= 768 ? "12px" : "16px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: window.innerWidth <= 768 ? "12px" : "14px",
                        color: "#666",
                        marginBottom: "6px",
                        fontWeight: "600",
                      }}
                    >
                      Note existente:
                    </div>
                    <div
                      style={{
                        overflow: "auto",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #e8e8e8",
                        padding: "6px",
                      }}
                    >
                      <CatalogElev
                        materiiLimited={[materieId || "undefined"]}
                        elevIdPassed={student.id}
                        open={true}
                        compact={true}
                      />
                    </div>
                  </div>

                  {/* Rubrici for grades */}
                  {tip === "nota" && rubrici?.length > 0 && (
                    <div
                      style={{
                        marginBottom:
                          window.innerWidth <= 768 ? "12px" : "16px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: window.innerWidth <= 768 ? "12px" : "14px",
                          color: "#666",
                          marginBottom: "6px",
                          fontWeight: "600",
                        }}
                      >
                        Rubrici:
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {rubrici.map((rubrica, index) => (
                          <Tag
                            key={index}
                            color="blue"
                            style={{
                              fontSize:
                                window.innerWidth <= 768 ? "10px" : "12px",
                              padding: "2px 6px",
                            }}
                          >
                            {rubrica}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Input Section */}
                  <div>
                    {tip === "absenta" ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: window.innerWidth <= 768 ? "8px" : "12px",
                          backgroundColor: "#ffffff",
                          borderRadius: "6px",
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <span
                          style={{
                            marginRight: "12px",
                            fontSize:
                              window.innerWidth <= 768 ? "14px" : "16px",
                            fontWeight: "600",
                          }}
                        >
                          Absent:
                        </span>
                        <Checkbox
                          checked={studentGrades[student.id]?.absent || false}
                          onChange={(e) =>
                            handleStudentChange(
                              student.id,
                              "absent",
                              e.target.checked
                            )
                          }
                          style={{
                            transform:
                              window.innerWidth <= 768
                                ? "scale(1.2)"
                                : "scale(1.4)",
                          }}
                        />
                      </div>
                    ) : (
                      <div>
                        <div
                          style={{
                            fontSize:
                              window.innerWidth <= 768 ? "12px" : "14px",
                            marginBottom: "8px",
                            fontWeight: "600",
                            color: "#666",
                          }}
                        >
                          Selectează nota:
                        </div>
                        <div
                          style={{
                            padding: window.innerWidth <= 768 ? "8px" : "12px",
                            backgroundColor: "#ffffff",
                            borderRadius: "6px",
                            border: "2px solid #e0e0e0",
                          }}
                        >
                          {notare === true ? (
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  window.innerWidth <= 768
                                    ? "repeat(5, 1fr)"
                                    : "repeat(10, 1fr)",
                                gap: window.innerWidth <= 768 ? "6px" : "8px",
                              }}
                            >
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((grade) => {
                                const getGradeColor = (gradeValue) => {
                                  if (gradeValue <= 4) return "#ff4d4f";
                                  if (gradeValue <= 6) return "#fa8c16";
                                  if (gradeValue <= 7) return "#faad14";
                                  if (gradeValue <= 9) return "#52c41a";
                                  return "#1890ff";
                                };

                                const gradeColor = getGradeColor(grade);
                                const isSelected =
                                  studentGrades[student.id]?.nota === grade;

                                return (
                                  <Button
                                    key={grade}
                                    onClick={() =>
                                      handleStudentChange(
                                        student.id,
                                        "nota",
                                        grade
                                      )
                                    }
                                    style={{
                                      backgroundColor: isSelected
                                        ? gradeColor
                                        : "transparent",
                                      borderColor: gradeColor,
                                      color: isSelected ? "#fff" : gradeColor,
                                      width:
                                        window.innerWidth <= 768
                                          ? "40px"
                                          : "45px",
                                      height:
                                        window.innerWidth <= 768
                                          ? "40px"
                                          : "45px",
                                      fontSize:
                                        window.innerWidth <= 768
                                          ? "14px"
                                          : "16px",
                                      fontWeight: "bold",
                                      borderWidth: "2px",
                                    }}
                                  >
                                    {grade}
                                  </Button>
                                );
                              })}
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: window.innerWidth <= 768 ? "6px" : "8px",
                              }}
                            >
                              {[
                                {
                                  value: "FB",
                                  label: "Foarte Bine",
                                  color: "#52c41a",
                                },
                                { value: "B", label: "Bine", color: "#1890ff" },
                                {
                                  value: "S",
                                  label: "Suficient",
                                  color: "#faad14",
                                },
                                {
                                  value: "I",
                                  label: "Insuficient",
                                  color: "#ff7875",
                                },
                              ].map((qual) => {
                                const isSelected =
                                  studentGrades[student.id]?.nota ===
                                  qual.value;
                                return (
                                  <Button
                                    key={qual.value}
                                    onClick={() =>
                                      handleStudentChange(
                                        student.id,
                                        "nota",
                                        qual.value
                                      )
                                    }
                                    style={{
                                      backgroundColor: isSelected
                                        ? qual.color
                                        : "transparent",
                                      borderColor: qual.color,
                                      color: isSelected ? "#fff" : qual.color,
                                      height:
                                        window.innerWidth <= 768
                                          ? "35px"
                                          : "40px",
                                      fontSize:
                                        window.innerWidth <= 768
                                          ? "11px"
                                          : "13px",
                                      fontWeight: "bold",
                                      borderWidth: "2px",
                                    }}
                                  >
                                    {qual.label}
                                  </Button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          // Step 2: Date, Summary and Final Confirmation
          <div
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* Date Section */}
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  display: "block",
                }}
              >
                Selectează data:
              </label>
              <ConfigProvider>
                <DatePicker
                  style={{ width: "100%" }}
                  format={dateFormat}
                  value={date}
                  onChange={setDate}
                  size="large"
                  disabledDate={(current) => {
                    const isOver21Days = (date) => {
                      const today = dayjs();
                      const diffInDays = today.diff(date, "day");
                      const isFuture = dayjs(date).isAfter(today, "day");

                      return (
                        (process.env.REACT_APP_LIMIT_DATE === "enable" &&
                          diffInDays > 21) ||
                        isFuture
                      );
                    };
                    return (
                      current &&
                      (current.day() === 0 ||
                        current.day() === 6 ||
                        (isOver21Days(current) &&
                          user?.type !== "admin" &&
                          fullAcces !== true &&
                          dupaTermen !== true &&
                          settings?.treiSapt !== true &&
                          user?.treiSapt !== true))
                    );
                  }}
                />
              </ConfigProvider>
            </div>

            {/* Existing grades warning for selected date */}
            {(() => {
              const selectedDate = date.format("DD/MM/YYYY");
              const studentsWithExistingGrades = Object.keys(studentGrades)
                .filter((elevId) =>
                  tip === "absenta"
                    ? studentGrades[elevId]?.absent
                    : studentGrades[elevId]?.nota
                )
                .map((elevId) => {
                  const existingGrades = (
                    gradesElevi[elevId]?.note || []
                  ).filter((n) => {
                    const gradeDate = dayjs(n.date).format("DD/MM/YYYY");
                    return (
                      gradeDate === selectedDate && n.materieId === materieId
                    );
                  });
                  return { elevId, existingGrades };
                })
                .filter((student) => student.existingGrades.length > 0);

              if (studentsWithExistingGrades.length > 0) {
                return (
                  <div
                    style={{
                      backgroundColor: "#fff2e8",
                      border: "2px solid #fa8c16",
                      borderRadius: "8px",
                      padding: "12px",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        marginBottom: "8px",
                        color: "#fa8c16",
                      }}
                    >
                      ⚠️ Atenție! Note existente pentru {selectedDate}:
                    </div>
                    {studentsWithExistingGrades.map(
                      ({ elevId, existingGrades }) => {
                        const student = eleviData?.find((e) => e.id === elevId);
                        return (
                          <div key={elevId} style={{ marginBottom: "4px" }}>
                            <strong>
                              {student?.numeDeFamilie} {student?.prenume}:
                            </strong>{" "}
                            {existingGrades.map((grade, index) => (
                              <span key={index}>
                                {grade.tip === "nota"
                                  ? `Notă: ${grade.nota}`
                                  : grade.tip === "absenta"
                                  ? "Absență"
                                  : grade.tip}
                                {grade.comentariu && ` - ${grade.comentariu}`}
                                {index < existingGrades.length - 1 ? ", " : ""}
                              </span>
                            ))}
                          </div>
                        );
                      }
                    )}
                  </div>
                );
              }
              return null;
            })()}

            {/* Comment Section */}
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  display: "block",
                }}
              >
                Comentariu general (opțional):
              </label>
              <TextArea
                placeholder="Comentariu pentru toți elevii selectați..."
                rows={3}
                value={comentariu}
                onChange={(e) => setComentariu(e.target.value)}
              />
            </div>

            {/* Detailed Summary */}
            <div
              style={{
                backgroundColor: "#f0f8ff",
                padding: "16px",
                borderRadius: "8px",
                border: "2px solid #1890ff",
                flex: 1,
                overflow: "auto",
              }}
            >
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "12px",
                  color: "#1890ff",
                }}
              >
                Rezumat Final:
              </div>

              {tip === "absenta" && (
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                    }}
                  >
                    Absențe pentru{" "}
                    {
                      Object.keys(studentGrades).filter(
                        (id) => studentGrades[id]?.absent
                      ).length
                    }{" "}
                    elevi:
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                  >
                    {Object.keys(studentGrades)
                      .filter((id) => studentGrades[id]?.absent)
                      .map((elevId) => {
                        const student = eleviData?.find((e) => e.id === elevId);
                        return (
                          <Tag
                            key={elevId}
                            color="red"
                            style={{ fontSize: "12px", padding: "4px 8px" }}
                          >
                            {student?.numeDeFamilie} {student?.prenume}
                          </Tag>
                        );
                      })}
                  </div>
                </div>
              )}

              {tip === "nota" && (
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                    }}
                  >
                    Note pentru{" "}
                    {
                      Object.keys(studentGrades).filter(
                        (id) => studentGrades[id]?.nota
                      ).length
                    }{" "}
                    elevi:
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                  >
                    {Object.keys(studentGrades)
                      .filter((id) => studentGrades[id]?.nota)
                      .map((elevId) => {
                        const student = eleviData?.find((e) => e.id === elevId);
                        const nota = studentGrades[elevId]?.nota;
                        const getGradeColor = (gradeValue) => {
                          if (notare === false) return "blue";
                          if (gradeValue <= 4) return "red";
                          if (gradeValue <= 6) return "orange";
                          if (gradeValue <= 7) return "gold";
                          if (gradeValue <= 9) return "green";
                          return "blue";
                        };
                        return (
                          <Tag
                            key={elevId}
                            color={getGradeColor(nota)}
                            style={{ fontSize: "12px", padding: "4px 8px" }}
                          >
                            {student?.numeDeFamilie} {student?.prenume}: {nota}
                          </Tag>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ModalTabularGrade;
